import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';

interface DatabaseConfig {
  name: string;
  version: number;
  encrypted: boolean;
  mode: string;
}

interface StorageStats {
  totalSize: number;
  usedSize: number;
  availableSize: number;
  tableStats: { [tableName: string]: { rowCount: number; size: number } };
}

class DatabaseService {
  private sqlite: SQLiteConnection;
  private db: SQLiteDBConnection | null = null;
  private isInitialized = false;
  private readonly dbConfig: DatabaseConfig = {
    name: 'seesor_offline',
    version: 1,
    encrypted: false,
    mode: 'no-encryption'
  };

  public readonly inspectingTypes = [
    'inspections', 'checklist_items', 'attachments'
  ];

  private readonly cacheTables = [
    'sync_queue', 'cache_metadata',
    ...this.inspectingTypes.map(t => `cached_${t}`)
  ]

  constructor() {
    this.sqlite = new SQLiteConnection(CapacitorSQLite);
  }

  async initialize(): Promise<void> {
    console.log('In DatabaseService initialize');

    if (this.isInitialized) return;

    // console.log('🔄 DatabaseService initializing...');
    try {
      // Check if platform supports SQLite
      if (Capacitor.getPlatform() === 'web') {
        // Web platform setup
        console.log('🌐 Initializing web store for SQLite...');
        await customElements.whenDefined('jeep-sqlite');
        await this.sqlite.initWebStore();
        console.log('✅ Web store initialized');
      }

      console.log('🔄 Creating/opening database connection...');
      // Create/open database
      this.db = await this.sqlite.createConnection(
        this.dbConfig.name,
        this.dbConfig.encrypted,
        this.dbConfig.mode,
        this.dbConfig.version,
        false
      );

      console.log('🔄 Opening database...');
      await this.db.open();
      await this.createTables();
      
      this.isInitialized = true;
      console.log('✅ DatabaseService initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize DatabaseService:', error);
      throw error;
    }
  }

  private async createTables(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const tableQueries = [
      // Sync queue table for pending operations
      `CREATE TABLE IF NOT EXISTS sync_queue (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        operation TEXT NOT NULL,
        table_name TEXT NOT NULL,
        record_id TEXT,
        data TEXT NOT NULL,
        created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
        retry_count INTEGER DEFAULT 0,
        status TEXT DEFAULT 'pending'
      )`,

      // Cache metadata table
      `CREATE TABLE IF NOT EXISTS cache_metadata (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL,
        expires_at DATETIME,
        created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
        updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
      )`,
      
      `CREATE TABLE IF NOT EXISTS cached_contracts (
        id INTEGER PRIMARY KEY,
        contract_id INTEGER,
        name TEXT,
        cached_at DATETIME DEFAULT CURRENT_TIMESTAMP
      )`,

      // Inspection data tables
      `CREATE TABLE IF NOT EXISTS cached_inspections (
        id INTEGER PRIMARY KEY,
        contract_id INTEGER,
        inspection_result_id INTEGER,
        group_id INTEGER,
        group_identifier TEXT,
        group_title TEXT,
        line_item_id INTEGER,
        line_item_title TEXT,
        line_item TEXT,
        site_id INTEGER,
        site_name TEXT,
        is_exceptional BOOLEAN,
        user_id INTEGER,
        inspector_name TEXT,
        area_of_expertise TEXT,
        area_of_expertise_id INTEGER,
        composite_score REAL,
        time_sensitivity TEXT,
        status TEXT,
        inspected_on DATETIME,
        scheduled_date DATETIME,
        line_item_primary_weight REAL,
        line_item_alternate_weight REAL,
        time_to_inspect INTEGER,
        method_of_inspection TEXT,
        sample_size TEXT,
        sample_selection TEXT,
        additional_instructions TEXT,
        major_requirement TEXT,
        related_info TEXT,
        estimated_quantity TEXT,
        performance_standard TEXT,
        cached_at DATETIME DEFAULT CURRENT_TIMESTAMP,
        updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
      )`,

      `CREATE TABLE IF NOT EXISTS cached_checklist_items (
        id INTEGER PRIMARY KEY,
        inspection_result_id INTEGER,
        checklist_item_id INTEGER,
        text TEXT,
        order_index INTEGER,
        score INTEGER,
        comment TEXT,
        weight REAL,
        results_analysis TEXT,
        cached_at DATETIME DEFAULT CURRENT_TIMESTAMP
      )`,

      `CREATE TABLE IF NOT EXISTS cached_attachments (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        parent_type TEXT NOT NULL,
        parent_id INTEGER NOT NULL,
        description TEXT,
        link TEXT,
        file_id INTEGER,
        file_name TEXT,
        file_type TEXT,
        file_size INTEGER,
        file_data TEXT,
        local_path TEXT,
        sync_status TEXT DEFAULT 'pending',
        cached_at DATETIME DEFAULT CURRENT_TIMESTAMP
      )`,
      
    ];

    for (const query of tableQueries) {
      await this.db.execute(query);
    }

    // Create indexes for better performance
    const indexQueries = [
      'CREATE INDEX IF NOT EXISTS idx_sync_queue_status ON sync_queue (status)',
      'CREATE INDEX IF NOT EXISTS idx_sync_queue_table ON sync_queue (table_name)',
      'CREATE INDEX IF NOT EXISTS idx_cache_metadata_expires ON cache_metadata (expires_at)',
      'CREATE INDEX IF NOT EXISTS idx_inspection_results_ir_id ON cached_inspections (inspection_result_id)',
      'CREATE INDEX IF NOT EXISTS idx_checklist_items_inspection ON cached_checklist_items (inspection_result_id)',
      'CREATE INDEX IF NOT EXISTS idx_attachments_parent ON cached_attachments (parent_type, parent_id)'
    ];

    for (const query of indexQueries) {
      await this.db.execute(query);
    }
  }

  // Generic CRUD operations
  async insert(table: string, data: any): Promise<number> {
    if (!this.db) throw new Error('Database not initialized');

    const keys = Object.keys(data);
    const values = Object.values(data);
    const placeholders = keys.map(() => '?').join(', ');
    
    const query = `INSERT INTO ${table} (${keys.join(', ')}) VALUES (${placeholders})`;
    const result = await this.db.run(query, values);
    
    return result.changes?.lastId || 0;
  }

  async select(table: string, where?: string, params?: any[]): Promise<any[]> {
    if (!this.db) throw new Error('Database not initialized');

    let query = `SELECT * FROM ${table}`;
    if (where) {
      query += ` WHERE ${where}`;
    }

    const result = await this.db.query(query, params);
    return result.values || [];
  }

  async update(table: string, data: any, where: string, params: any[]): Promise<number> {
    if (!this.db) throw new Error('Database not initialized');

    const updates = Object.keys(data).map(key => `${key} = ?`).join(', ');
    const values = [...Object.values(data), ...params];
    
    const query = `UPDATE ${table} SET ${updates} WHERE ${where}`;
    const result = await this.db.run(query, values);
    
    return result.changes?.changes || 0;
  }

  async delete(table: string, where: string, params: any[]): Promise<number> {
    if (!this.db) throw new Error('Database not initialized');

    const query = `DELETE FROM ${table} WHERE ${where}`;
    const result = await this.db.run(query, params);
    
    return result.changes?.changes || 0;
  }

  // Sync queue operations
  async addToSyncQueue(operation: string, tableName: string, recordId: string, data: any): Promise<number> {
    return await this.insert('sync_queue', {
      operation,
      table_name: tableName,
      record_id: recordId,
      data: JSON.stringify(data),
      status: 'pending'
    });
  }

  async getSyncQueue(status = 'pending'): Promise<any[]> {
    return await this.select('sync_queue', 'status = ?', [status]);
  }

  async updateSyncQueueStatus(id: number, status: string, retryCount?: number): Promise<void> {
    const updateData: any = { status };
    if (retryCount !== undefined) {
      updateData.retry_count = retryCount;
    }
    
    await this.update('sync_queue', updateData, 'id = ?', [id]);
  }

  async clearSyncQueue(): Promise<void> {
    await this.delete('sync_queue', 'status = ?', ['completed']);
  }

  // Cache operations
  async setCacheValue(key: string, value: any, expiresIn?: number): Promise<void> {
    const expiresAt = expiresIn ? new Date(Date.now() + expiresIn * 1000) : null;
    
    const existing = await this.select('cache_metadata', 'key = ?', [key]);
    
    if (existing.length > 0) {
      await this.update('cache_metadata', {
        value: JSON.stringify(value),
        expires_at: expiresAt?.toISOString(),
        updated_at: new Date().toISOString()
      }, 'key = ?', [key]);
    } else {
      await this.insert('cache_metadata', {
        key,
        value: JSON.stringify(value),
        expires_at: expiresAt?.toISOString()
      });
    }
  }

  async getCacheValue(key: string): Promise<any | null> {
    const results = await this.select('cache_metadata', 'key = ?', [key]);
    
    if (results.length === 0) return null;
    
    const record = results[0];
    
    // Check if expired
    if (record.expires_at && new Date(record.expires_at) < new Date()) {
      await this.delete('cache_metadata', 'key = ?', [key]);
      return null;
    }
    
    try {
      return JSON.parse(record.value);
    } catch (error) {
      console.error('Error parsing cached value:', error);
      return null;
    }
  }


  // Storage statistics
  async getStorageStats(): Promise<StorageStats> {
    if (!this.db) throw new Error('Database not initialized');

    const tableStats: { [tableName: string]: { rowCount: number; size: number } } = {};
    let totalSize = 0;

    for (const table of this.cacheTables) {
      try {
        const countResult = await this.db.query(`SELECT COUNT(*) as count FROM ${table}`);
        const rowCount = countResult.values?.[0]?.count || 0;
        
        // Estimate size (rough calculation)
        const sizeEstimate = rowCount * 100; // rough estimate of bytes per row
        
        tableStats[table] = { rowCount, size: sizeEstimate };
        totalSize += sizeEstimate;
      } catch (error) {
        console.warn(`Error getting stats for table ${table}:`, error);
        tableStats[table] = { rowCount: 0, size: 0 };
      }
    }

    // Get available storage (approximate)
    const availableSize = this.getAvailableStorageSpace();

    return {
      totalSize,
      usedSize: totalSize,
      availableSize,
      tableStats
    };
  }

  private getAvailableStorageSpace(): number {
    // This is an approximation - actual implementation would depend on platform
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(estimate => {
        return (estimate.quota || 0) - (estimate.usage || 0);
      });
    }
    
    // Fallback estimation
    return 50 * 1024 * 1024; // 50MB approximation
  }

  // Database maintenance
  async vacuum(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.execute('VACUUM');
  }

  async clearExpiredCache(): Promise<number> {
    const deleted = await this.delete('cache_metadata', 'expires_at < ?', [new Date().toISOString()]);
    console.log(`🧹 Cleared ${deleted} expired cache entries`);
    return deleted;
  }

  async clearOldSyncQueue(daysOld = 7): Promise<number> {
    const cutoffDate = new Date(Date.now() - daysOld * 24 * 60 * 60 * 1000).toISOString();
    const deleted = await this.delete('sync_queue', 'created_at < ? AND status = ?', [cutoffDate, 'completed']);
    console.log(`🧹 Cleared ${deleted} old sync queue entries`);
    return deleted;
  }

  // Connection management
  async close(): Promise<void> {
    if (this.db) {
      await this.db.close();
      this.db = null;
      this.isInitialized = false;
    }
  }

  isReady(): boolean {
    return this.isInitialized && this.db !== null;
  }
}

// Export singleton instance
export const databaseService = new DatabaseService();
export default databaseService;